import { useCallback } from "react";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import { useTranslation } from "@shared/utils/internationalization";
import type { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import type { BasketUpgradeInfo } from "@shared/modules/basket/domain/services/BasketUpgradesService";

const notifier = NotifierFactory.createNotifier();

type UseItemGroupUpgradeOptions = {
  onAfterUpgrade?: (opts: { upgradeFrom: string; upgradeTo: string }) => void;
};

type UseItemGroupUpgradeReturn = {
  handleUpgrade: (
    item: TicketItemGroupType,
    upgradeInfo: BasketUpgradeInfo
  ) => Promise<void>;
  isBasketUpdating: boolean;
};

export function useItemGroupUpgrade(
  options?: UseItemGroupUpgradeOptions
): UseItemGroupUpgradeReturn {
  const { t } = useTranslation("purchases");
  const { upgradeItemGroup, isBasketUpdating } = usePublicBasket();

  const handleUpgrade = useCallback(
    async (item: TicketItemGroupType, upgradeInfo: BasketUpgradeInfo) => {
      const upgradeOption = upgradeInfo.upgradeOptions?.[0];
      const { quantity = 1 } = item;

      const targetItemType =
        upgradeOption?.targetItemType ?? upgradeInfo.itemType;

      if (!targetItemType) {
        recordAppError(new Error("Item type is undefined in upgrade options"));
        return;
      }

      await upgradeItemGroup(
        item,
        {
          itemData: upgradeOption?.itemData,
          itemType: targetItemType,
          quantity,
        },
        { shouldPersist: true }
      );
      notifier.success(
        t("Your items have been upgraded!", { count: quantity })
      );
      options?.onAfterUpgrade?.({
        upgradeFrom: item.description ?? String(item.type),
        upgradeTo: upgradeOption?.name ?? "",
      });
    },
    [upgradeItemGroup, t, options?.onAfterUpgrade]
  );

  return { handleUpgrade, isBasketUpdating };
}
